import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Camera, Image as ImageIcon, Plus, Repeat, X, Check, Clock, Shirt, Trash2, MessageCircle, MoreVertical, Ban } from 'lucide-react';
import { shrinkPhoto } from './kitPhoto';
import KitChat from './KitChat';
import './KitSwap.css';

/**
 * Kit Swap (B-45) — the second tab of Shop. Free outgrown kit, passed on
 * between members of the club: list it with a photo, someone taps
 * "I'd like this", it's left behind the bar. No prices anywhere.
 *
 * Views: Browse (grid + filters + detail) · My listings · List an item.
 * All data from /api/clubs/:slug/kit (routes/kitSwap.js).
 *
 * v39: once an item is claimed, the lister and the claimer get a private
 * chat in the detail sheet (KitChat.jsx). Listing objects carry chat_state
 * ('open' | 'read_only' | null — null unless you're one of the two) and
 * unread_messages, shown as a badge on the card / row.
 *
 * v40: Block — the ⋮ in the item sheet → "Block {name}" (the other person on
 * that listing; the server works out who, the client never sees a user id).
 * Unblock under My listings → Blocked people. Claims someone else ended
 * (relist / take down / admin / 14-day lapse) show under "Claims that ended".
 */

export const ITEM_TYPES = [
  { key: 'shirt', label: 'Shirt' },
  { key: 'shorts', label: 'Shorts' },
  { key: 'socks', label: 'Socks' },
  { key: 'boots', label: 'Boots' },
  { key: 'training_top', label: 'Training top' },
  { key: 'jacket', label: 'Jacket/hoodie' },
  { key: 'bag', label: 'Bag' },
  { key: 'other', label: 'Other' },
];
const TYPE_LABEL = Object.fromEntries(ITEM_TYPES.map(t => [t.key, t.label]));
const CONDITIONS = [
  { key: 'like_new', label: 'Like new' },
  { key: 'good', label: 'Good' },
  { key: 'worn', label: 'Worn but usable' },
];
const CONDITION_LABEL = Object.fromEntries(CONDITIONS.map(c => [c.key, c.label]));
const SIZE_GROUPS = [
  { label: 'Junior (age)', sizes: ['Age 5–6', 'Age 7–8', 'Age 9–10', 'Age 11–12', 'Age 13–14'] },
  { label: 'Adult', sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] },
  { label: 'Boots (UK)', sizes: ['UK 5 junior', 'UK 6 junior', 'UK 7 junior', 'UK 8 junior', 'UK 9 junior', 'UK 10 junior', 'UK 11 junior', 'UK 12 junior', 'UK 13 junior', 'UK 1', 'UK 2', 'UK 3', 'UK 4', 'UK 5', 'UK 6', 'UK 7', 'UK 8', 'UK 9', 'UK 10', 'UK 11', 'UK 12'] },
];
const OTHER_SIZE = '__other__';
const NOTE_MAX = 200;
const TITLE_MIN = 3;
const TITLE_MAX = 60;
const DEFAULT_HANDOVER = 'Left behind the bar — ask for it by name';

/** "Ask at the bar for Sam T.'s bag." — or the club's own wording. */
export const handoverLine = (name, handover) => (
  !handover || handover === DEFAULT_HANDOVER
    ? `Ask at the bar for ${name}'s bag.`
    : `Ask for ${name}'s bag — ${handover}`
);

/** The item's name: the lister's title, or — for listings made before titles
 *  existed (title null) — the old "Shirt · Age 9–10" label. */
const itemLabel = (l) => (l.title || `${TYPE_LABEL[l.item_type] || 'Item'} · ${l.size}`);
/** The line under a titled item: "Shirt · Age 9–10 · Good". */
const itemSubline = (l) => [TYPE_LABEL[l.item_type] || 'Item', l.size, CONDITION_LABEL[l.condition]].filter(Boolean).join(' · ');

/** The confirm shown before blocking (Stuart, 7 Oct 2026). */
const BLOCK_CONFIRM = 'They won\'t be able to claim your items or message you, and you won\'t see their listings. They won\'t be told.';

const errText = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
};

// The photo endpoint needs the Bearer token, so an <img src> can't fetch it
// directly. Fetch as a blob (the server marks it private + cacheable for a
// week; TanStack keeps it for the session) and show an object URL.
// Photos load only as they come near the screen (QA: a full grid fetched at
// once could hit the API's 100-requests-a-minute limit, which is per IP —
// and everyone on the clubhouse Wi-Fi shares one).
// B-49: exported for the Kit Shop + Shop manager photos (same pipeline);
// `Placeholder` is the icon shown while loading / when there's no photo.
export function KitPhoto({ url, alt, className, Placeholder = Shirt }) {
  const boxRef = useRef(null);
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near || !boxRef.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(boxRef.current);
    return () => io.disconnect();
  }, [near]);
  const { data: blob, isError } = useQuery({
    queryKey: ['kit-photo', url],
    queryFn: () => axios.get(url, { responseType: 'blob' }).then(r => r.data),
    enabled: near && !!url,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!blob) return undefined;
    const u = URL.createObjectURL(blob);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return (
    <div ref={boxRef} className={`kit-photo ${className || ''}`}>
      {src ? <img src={src} alt={alt} /> : <Placeholder size={36} className={isError || !url ? 'kit-photo-missing' : 'kit-photo-loading'} />}
    </div>
  );
}

/** Small unread-messages badge (v39). Renders nothing at 0 / null. */
const UnreadBadge = ({ n }) => (n > 0
  ? <span className="kit-unread" aria-label={`${n} unread message${n === 1 ? '' : 's'}`}>{n > 9 ? '9+' : n}</span>
  : null);

const StatusBadge = ({ status }) => {
  const map = {
    available: ['badge-green', 'Available'],
    claimed: ['badge-gold', 'Claimed'],
    collected: ['badge-silver', 'Collected'],
    expired: ['badge-silver', 'Closed'],
    removed: ['badge-silver', 'Taken down'],
  };
  const [cls, label] = map[status] || ['badge-silver', status];
  return <span className={`badge ${cls}`}>{label}</span>;
};

// ─────────────────────────────────────────────────────────────────────
// List an item
// ─────────────────────────────────────────────────────────────────────

function ListItemForm({ clubSlug, handover, onDone, onCancel }) {
  const qc = useQueryClient();
  const takeRef = useRef(null);
  const pickRef = useRef(null);
  const [photo, setPhoto] = useState(null); // Blob (already shrunk)
  const [preview, setPreview] = useState(null);
  const [shrinking, setShrinking] = useState(false);
  const [title, setTitle] = useState('');
  const [itemType, setItemType] = useState('');
  const [sizePick, setSizePick] = useState('');
  const [sizeOther, setSizeOther] = useState('');
  const [condition, setCondition] = useState('');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError(''); setShrinking(true);
    try {
      const blob = await shrinkPhoto(file);
      setPhoto(blob);
      setPreview(URL.createObjectURL(blob));
    } catch (err) {
      setError(err.message || 'Could not read that photo.');
    } finally { setShrinking(false); }
  };

  const size = sizePick === OTHER_SIZE ? sizeOther.trim() : sizePick;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const cleanTitle = title.replace(/\s+/g, ' ').trim();
    if (!cleanTitle) return setError('Give your item a title, e.g. Yellow Puma boots.');
    if (cleanTitle.length < TITLE_MIN) return setError(`Make the title at least ${TITLE_MIN} characters.`);
    if (cleanTitle.length > TITLE_MAX) return setError(`Keep the title to ${TITLE_MAX} characters.`);
    if (!photo) return setError('Add a photo of the kit.');
    if (!itemType) return setError('Choose what the item is.');
    if (!size) return setError('Add a size.');
    if (!condition) return setError('Choose the condition.');
    if (!confirmed) return setError('Tick to confirm the photo shows the kit only, no people.');
    const fd = new FormData();
    fd.append('title', cleanTitle);
    fd.append('item_type', itemType);
    fd.append('size', size);
    fd.append('condition', condition);
    if (note.trim()) fd.append('note', note.trim());
    fd.append('confirm_no_people', 'true');
    fd.append('photo', photo, 'kit.jpg');
    setPosting(true);
    try {
      await axios.post(`/clubs/${clubSlug}/kit`, fd);
      qc.invalidateQueries({ queryKey: ['kit-swap', clubSlug] });
      onDone();
    } catch (err) {
      setError(errText(err, 'Could not post your item — try again.'));
    } finally { setPosting(false); }
  };

  return (
    <form className="kit-form card" onSubmit={submit}>
      <div className="kit-form-head">
        <h3>List an item</h3>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}><X size={14} /> Cancel</button>
      </div>
      <p className="kit-free-note">Kit Swap is <strong>free only</strong> — no prices, no selling. {String(handover || '').replace(/[.!\s]+$/, '')}.</p>

      <div className="form-group">
        <label className="label" htmlFor="kit-title">Title</label>
        <input id="kit-title" className="input kit-title-input" type="text" aria-required="true" maxLength={TITLE_MAX}
          autoComplete="off" enterKeyHint="next" placeholder="e.g. Yellow Puma boots"
          value={title} onChange={e => setTitle(e.target.value)} />
        <p className="kit-hint" style={{ textAlign: 'right' }}>{title.length}/{TITLE_MAX}</p>
      </div>

      <div className="form-group">
        <label className="label">Photo</label>
        {preview
          ? (
            <div className="kit-form-preview">
              <img src={preview} alt="Your kit" />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setPhoto(null); setPreview(null); }}>Change photo</button>
            </div>
          ) : (
            <div className="kit-form-photo-btns">
              <button type="button" className="btn btn-ghost" onClick={() => takeRef.current?.click()} disabled={shrinking}><Camera size={16} /> Take a photo</button>
              <button type="button" className="btn btn-ghost" onClick={() => pickRef.current?.click()} disabled={shrinking}><ImageIcon size={16} /> Choose a photo</button>
            </div>
          )}
        {shrinking && <p className="kit-hint">Getting your photo ready…</p>}
        <input ref={takeRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} />
        <input ref={pickRef} type="file" accept="image/*" hidden onChange={onFile} />
      </div>

      <div className="form-group">
        <label className="label">What is it?</label>
        <div className="pill-group">
          {ITEM_TYPES.map(t => (
            <button type="button" key={t.key} className={`pill-btn ${itemType === t.key ? 'active' : ''}`} onClick={() => setItemType(t.key)}>{t.label}</button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="label" htmlFor="kit-size">Size</label>
        <select id="kit-size" className="input" value={sizePick} onChange={e => setSizePick(e.target.value)}>
          <option value="">Choose a size</option>
          {SIZE_GROUPS.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.sizes.map(s => <option key={s} value={s}>{s}</option>)}
            </optgroup>
          ))}
          <option value={OTHER_SIZE}>Something else — I'll type it</option>
        </select>
        {sizePick === OTHER_SIZE && (
          <input className="input" style={{ marginTop: 8 }} maxLength={40} placeholder="e.g. 32in waist, Age 3–4" value={sizeOther} onChange={e => setSizeOther(e.target.value)} />
        )}
      </div>

      <div className="form-group">
        <label className="label">Condition</label>
        <div className="pill-group">
          {CONDITIONS.map(c => (
            <button type="button" key={c.key} className={`pill-btn ${condition === c.key ? 'active' : ''}`} onClick={() => setCondition(c.key)}>{c.label}</button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="label" htmlFor="kit-note">Note <span className="optional">(optional)</span></label>
        <textarea id="kit-note" className="input" rows={2} maxLength={NOTE_MAX} value={note}
          placeholder="e.g. Home shirt, worn one season" onChange={e => setNote(e.target.value)} />
        <p className="kit-hint" style={{ textAlign: 'right' }}>{note.length}/{NOTE_MAX}</p>
      </div>

      <label className="kit-tick">
        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
        <span>Photo shows the kit only, no people</span>
      </label>

      {error && <div className="kit-error">{error}</div>}
      <button type="submit" className="btn btn-primary kit-wide" disabled={posting || shrinking}>
        {posting ? 'Posting…' : 'Post'}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Item detail (claim / remove)
// ─────────────────────────────────────────────────────────────────────

function ItemDetail({ clubSlug, item, viewer, handover, onClose, onChanged, onBlocked }) {
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState('');
  const [justClaimed, setJustClaimed] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [reason, setReason] = useState('');

  const act = async (path, body) => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(`/clubs/${clubSlug}/kit/${item.id}/${path}`, body);
      onChanged();
      return r.data;
    } catch (err) {
      setError(errText(err, 'Something went wrong — try again.'));
      onChanged();
      return null;
    } finally { setBusy(false); }
  };

  const claim = async () => { if (await act('claim')) setJustClaimed(true); };
  const letGo = async () => {
    if (!window.confirm('Let this go back to everyone else?')) return;
    if (await act('release')) onClose();
  };
  // v40: who "Block" would block — the claimer of my listing, else the
  // lister. Only for people who can act (adults), only when there's someone
  // on the other side.
  const blockName = item.is_mine ? item.claimer_name : item.lister_name;
  const canBlock = !!(viewer?.can_act && blockName);
  const block = async () => {
    setMenuOpen(false);
    if (!window.confirm(`Block ${blockName}?\n\n${BLOCK_CONFIRM}`)) return;
    const out = await act('block');
    if (out) { onBlocked?.(out.block?.name || blockName); onClose(); }
  };
  const remove = async () => {
    if (!reason.trim()) { setError('Give a reason — the person who listed it will see it.'); return; }
    if (await act('remove', { reason: reason.trim() })) onClose();
  };

  // Only a live claim shows the handover + "don't need it" (an item opened
  // from "Recently collected" is the claimer's too, but done with — v39).
  const mineClaim = (item.claimed_by_me && item.status === 'claimed') || justClaimed;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card kit-detail" onClick={e => e.stopPropagation()}>
        <button type="button" className="kit-detail-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        {canBlock && (
          <div className="kit-more">
            <button type="button" className="kit-more-btn" aria-label="More options" aria-haspopup="menu" aria-expanded={menuOpen}
              onClick={() => setMenuOpen(o => !o)}><MoreVertical size={18} /></button>
            {menuOpen && (
              <div className="kit-more-menu" role="menu">
                <button type="button" role="menuitem" className="btn-danger-text" onClick={block} disabled={busy}>
                  <Ban size={14} /> Block {blockName}
                </button>
              </div>
            )}
          </div>
        )}
        <KitPhoto url={item.photo_url} alt={itemLabel(item)} className="kit-photo-lg" />
        <div className="kit-detail-body">
          <div className="kit-detail-title">
            <h2>{itemLabel(item)}</h2>
            {!justClaimed && <StatusBadge status={item.status} />}
          </div>
          <p className="kit-detail-meta">
            {item.title ? itemSubline(item) : CONDITION_LABEL[item.condition]} · listed by {item.lister_name}
          </p>
          {item.note && <p className="kit-detail-note">{item.note}</p>}

          {error && <div className="kit-error">{error}</div>}

          {mineClaim ? (
            <div className="kit-handover">
              <Check size={18} />
              <div>
                <strong>{justClaimed ? 'It\'s yours.' : 'You\'ve claimed this.'}</strong>
                <p>{handoverLine(item.lister_name, handover)}</p>
                <button type="button" className="btn btn-ghost btn-sm" onClick={letGo} disabled={busy}>I don't need it any more</button>
              </div>
            </div>
          ) : item.is_mine ? (
            <p className="kit-hint">This is your listing — manage it under My listings.</p>
          ) : item.status === 'available' ? (
            viewer?.can_act
              ? <button type="button" className="btn btn-primary kit-wide" onClick={claim} disabled={busy}>{busy ? 'One moment…' : 'I\'d like this'}</button>
              : <p className="kit-hint">Claiming kit is for adult members of the club.</p>
          ) : item.status === 'collected' ? (
            <p className="kit-claimed-line">Collected — passed on.</p>
          ) : (
            <p className="kit-claimed-line">Claimed — someone's collecting this one.</p>
          )}

          {(item.chat_state || justClaimed) && <KitChat clubSlug={clubSlug} listingId={item.id} />}

          {viewer?.can_moderate && !item.is_mine && (
            <div className="kit-admin">
              {!removing
                ? <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => setRemoving(true)}><Trash2 size={14} /> Remove listing</button>
                : (
                  <>
                    <label className="label" htmlFor="kit-reason">Reason (the person who listed it sees this)</label>
                    <input id="kit-reason" className="input" maxLength={200} value={reason} placeholder="e.g. Photo shows a child" onChange={e => setReason(e.target.value)} />
                    <div className="kit-row">
                      <button type="button" className="btn btn-primary btn-sm" onClick={remove} disabled={busy}>Remove</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRemoving(false)}>Cancel</button>
                    </div>
                  </>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// My listings
// ─────────────────────────────────────────────────────────────────────

const ENDED_TEXT = {
  lister_ended: (c) => `${c.lister_name} ended your claim.`,
  taken_down: (c) => `${c.lister_name} took it down — your claim has ended.`,
  admin_removed: () => 'A club admin removed it — your claim has ended.',
  lapsed: () => 'It wasn\'t collected within 14 days, so it went back up for everyone.',
};

function MyListings({ clubSlug, onList, onOpen, onSee, initialChatId }) {
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['kit-swap', clubSlug, 'mine'],
    queryFn: () => axios.get(`/clubs/${clubSlug}/kit/mine`).then(r => r.data),
  });
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  // v40: people I've blocked (only people who can act can block).
  const { data: blockData, refetch: refetchBlocks } = useQuery({
    queryKey: ['kit-swap', clubSlug, 'blocks'],
    queryFn: () => axios.get(`/clubs/${clubSlug}/kit/blocks`).then(r => r.data),
    enabled: !!data?.viewer?.can_act,
  });
  const blocks = blockData?.blocks || [];
  const unblock = async (b) => {
    if (!window.confirm(`Unblock ${b.name}? You'll see each other's listings again.`)) return;
    setBusyId(b.id); setError('');
    try {
      await axios.delete(`/clubs/${clubSlug}/kit/blocks/${b.id}`);
    } catch (err) {
      setError(errText(err, 'Could not unblock — try again.'));
    } finally {
      setBusyId(null);
      refetchBlocks();
      qc.invalidateQueries({ queryKey: ['kit-swap', clubSlug] });
    }
  };

  // A tap on a chat push lands here with ?chat=<listingId>: open that item's
  // sheet (once) when it's one of mine or one I've claimed.
  const openedChatRef = useRef(false);
  useEffect(() => {
    if (!initialChatId || openedChatRef.current || !data) return;
    openedChatRef.current = true;
    const hit = [...(data.claims || []), ...(data.recent_claims || []), ...(data.listings || [])].find(l => l.id === initialChatId && l.chat_state);
    if (hit) onOpen(hit);
  }, [initialChatId, data, onOpen]);

  const messagesBtn = (l) => (l.chat_state ? (
    <button type="button" className="btn btn-ghost btn-sm kit-msg-btn" onClick={() => onOpen(l)}>
      <MessageCircle size={14} /> Messages <UnreadBadge n={l.unread_messages} />
    </button>
  ) : null);

  const act = async (id, path, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(id); setError('');
    try {
      await axios.post(`/clubs/${clubSlug}/kit/${id}/${path}`);
    } catch (err) {
      setError(errText(err, 'Something went wrong — try again.'));
    } finally {
      setBusyId(null);
      qc.invalidateQueries({ queryKey: ['kit-swap', clubSlug] });
      refetch();
    }
  };

  if (isLoading) return <div className="page-loading"><div className="spinner" /></div>;
  if (!data) return null;
  const { listings = [], claims = [], recent_claims: recentClaims = [], ended_claims: endedClaims = [], handover_text: handover, viewer } = data;

  return (
    <div className="kit-mine">
      {error && <div className="kit-error">{error}</div>}

      {claims.length > 0 && (
        <section>
          <h3 className="kit-section-title">Kit you've claimed</h3>
          {claims.map(c => (
            <div key={c.id} className="kit-row-card card">
              <KitPhoto url={c.photo_url} alt="" className="kit-photo-thumb" />
              <div className="kit-row-main">
                <strong className="kit-item-name">{itemLabel(c)}</strong>
                {c.title && <p className="kit-subline">{itemSubline(c)}</p>}
                <p>{handoverLine(c.lister_name, handover)}</p>
                <div className="kit-row">
                  {messagesBtn(c)}
                  <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === c.id}
                    onClick={() => act(c.id, 'release', 'Let this go back to everyone else?')}>I don't need it any more</button>
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {endedClaims.length > 0 && (
        <section>
          <h3 className="kit-section-title">Claims that ended</h3>
          <p className="kit-hint">Shown here for a week.</p>
          {endedClaims.map(c => (
            <div key={c.id} className="kit-row-card card">
              <div className="kit-row-main">
                <strong className="kit-item-name">{itemLabel(c)}</strong>
                <p className="kit-hint">{(ENDED_TEXT[c.reason] || (() => 'Your claim has ended.'))(c)}</p>
                {c.still_listed && (
                  <div className="kit-row">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => onSee(c.id)}>See it</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {recentClaims.some(c => c.chat_state) && (
        <section>
          <h3 className="kit-section-title">Recently collected</h3>
          {recentClaims.filter(c => c.chat_state).map(c => (
            <div key={c.id} className="kit-row-card card">
              <KitPhoto url={c.photo_url} alt="" className="kit-photo-thumb" />
              <div className="kit-row-main">
                <strong className="kit-item-name">{itemLabel(c)}</strong>
                <p className="kit-hint">Collected — your messages with {c.lister_name} stay readable for a week.</p>
                <div className="kit-row">{messagesBtn(c)}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      <section>
        <div className="kit-section-head">
          <h3 className="kit-section-title">Your listings</h3>
          {viewer?.can_act && (
            <span className="kit-hint">{viewer.live_count} of {viewer.max_live} live</span>
          )}
        </div>
        {listings.length === 0 && (
          <div className="empty-state kit-empty">
            <p>Nothing listed yet.</p>
            {viewer?.can_act && <button type="button" className="btn btn-primary btn-sm" onClick={onList}><Plus size={14} /> List an item</button>}
          </div>
        )}
        {listings.map(l => {
          const left = daysLeft(l.expires_at);
          return (
            <div key={l.id} className="kit-row-card card">
              <KitPhoto url={l.photo_url} alt="" className="kit-photo-thumb" />
              <div className="kit-row-main">
                <div className="kit-row-title">
                  <strong className="kit-item-name">{itemLabel(l)}</strong>
                  <StatusBadge status={l.status} />
                </div>
                {l.title && <p className="kit-subline">{itemSubline(l)}</p>}
                {l.status === 'claimed' && (
                  <>
                    <p><strong>{l.claimer_name}</strong> would like this — leave it for them. <span className="kit-hint">({handover})</span></p>
                    <div className="kit-row">
                      {messagesBtn(l)}
                      <button type="button" className="btn btn-primary btn-sm" disabled={busyId === l.id} onClick={() => act(l.id, 'collect')}>Collected</button>
                      <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === l.id}
                        onClick={() => act(l.id, 'release', `Put this back up for everyone? ${l.claimer_name} will lose their claim.`)}>Not collected — relist</button>
                      <button type="button" className="btn btn-ghost btn-sm btn-danger-text" disabled={busyId === l.id}
                        onClick={() => act(l.id, 'withdraw', `Take this listing down? ${l.claimer_name} will lose their claim.`)}>Take down</button>
                    </div>
                    <p className="kit-hint">If you don't mark it, it goes back up for everyone 14 days after the claim.</p>
                  </>
                )}
                {l.status === 'available' && (
                  <>
                    <p className="kit-hint"><Clock size={12} /> Closes in {left} day{left === 1 ? '' : 's'}{l.auto_released_at ? ' · back up after an uncollected claim' : ''}</p>
                    <div className="kit-row">
                      <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === l.id} onClick={() => act(l.id, 'renew')}><Repeat size={14} /> Renew for 60 days</button>
                      <button type="button" className="btn btn-ghost btn-sm btn-danger-text" disabled={busyId === l.id}
                        onClick={() => act(l.id, 'withdraw', 'Take this listing down?')}>Take down</button>
                    </div>
                  </>
                )}
                {l.status === 'expired' && (
                  <>
                    <p className="kit-hint">Nobody claimed it within 60 days, so it closed.</p>
                    {viewer?.can_act && (
                      <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === l.id} onClick={() => act(l.id, 'renew')}><Repeat size={14} /> Put it back up</button>
                    )}
                  </>
                )}
                {l.status === 'collected' && l.chat_state && <div className="kit-row">{messagesBtn(l)}</div>}
                {l.status === 'removed' && l.removed_by_admin && (
                  <p className="kit-removed">A club admin removed this. Reason: {l.removed_reason}</p>
                )}
              </div>
            </div>
          );
        })}
        {data.truncated && <p className="kit-hint">Showing your {data.cap} most recent listings.</p>}
      </section>

      {blocks.length > 0 && (
        <section>
          <h3 className="kit-section-title">Blocked people</h3>
          <p className="kit-hint">You don't see each other's listings and can't message each other. They haven't been told.</p>
          {blocks.map(b => (
            <div key={b.id} className="kit-row-card card kit-block-row">
              <span className="kit-item-name">{b.name}</span>
              <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === b.id} onClick={() => unblock(b)}>Unblock</button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// The tab
// ─────────────────────────────────────────────────────────────────────

export default function KitSwap({ clubSlug, initialView = 'browse', initialChatId = null, initialItemId = null }) {
  const qc = useQueryClient();
  const [view, setView] = useState(initialChatId ? 'mine' : initialView); // browse | mine | new
  const [type, setType] = useState('all');
  const [size, setSize] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [mineItem, setMineItem] = useState(null); // detail sheet opened from My listings (v39 chat)
  const [notice, setNotice] = useState('');        // v40: "You blocked …"

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['kit-swap', clubSlug, 'list', type, size],
    queryFn: () => axios.get(`/clubs/${clubSlug}/kit`, { params: { type, size } }).then(r => r.data),
    // Always loaded (not just on Browse): the intro line, the List button
    // and the view-only notice all need the club's wording + viewer rights.
    placeholderData: (prev) => prev,
  });
  const viewer = data?.viewer;
  const handover = data?.handover_text || DEFAULT_HANDOVER;
  const listings = useMemo(() => data?.listings || [], [data]);
  const open = openId ? listings.find(l => l.id === openId) : null;

  const refresh = () => qc.invalidateQueries({ queryKey: ['kit-swap', clubSlug] });

  // v40: a "your claim has ended" push lands with ?item=<id>. Open it once if
  // it's on the grid; if it isn't (claimed by someone else, taken down…),
  // just stay on Kit Swap.
  const openedItemRef = useRef(false);
  useEffect(() => {
    if (!initialItemId || openedItemRef.current || !data || view !== 'browse') return;
    openedItemRef.current = true;
    if (listings.some(l => l.id === initialItemId)) setOpenId(initialItemId);
  }, [initialItemId, data, listings, view]);

  const seeItem = (id) => { setType('all'); setSize('all'); setView('browse'); setOpenId(id); };
  const onBlocked = (name) => {
    setNotice(`You blocked ${name}. You won't see each other's listings — undo it under My listings → Blocked people.`);
    refresh();
  };

  if (isError) {
    return <div className="empty-state">{errText(error, 'Kit Swap isn\'t available right now.')}</div>;
  }

  return (
    <div className="kit-swap">
      <p className="kit-intro">
        Good outgrown kit, free to anyone at the club. Tap <strong>I'd like this</strong> and it's yours — {(handover.charAt(0).toLowerCase() + handover.slice(1)).replace(/[.!\s]+$/, '')}.
      </p>

      <div className="kit-views">
        <div className="pill-group">
          <button type="button" className={`pill-btn ${view === 'browse' ? 'active' : ''}`} onClick={() => setView('browse')}>Browse</button>
          <button type="button" className={`pill-btn ${view === 'mine' ? 'active' : ''}`} onClick={() => setView('mine')}>My listings</button>
        </div>
        {viewer?.can_act !== false && view !== 'new' && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setView('new')}><Plus size={14} /> List an item</button>
        )}
      </div>

      {notice && (
        <div className="kit-note kit-notice" role="status">
          <span>{notice}</span>
          <button type="button" className="kit-notice-close" aria-label="Dismiss" onClick={() => setNotice('')}><X size={14} /></button>
        </div>
      )}

      {viewer && !viewer.can_act && view === 'browse' && (
        <div className="kit-note">
          {viewer.reason === 'staff_view_only'
            ? 'You\'re here as Marakin staff — you can look and remove listings, but not list or claim.'
            : 'Listing and claiming kit is for adult members of the club.'}
        </div>
      )}

      {view === 'new' && (
        <ListItemForm clubSlug={clubSlug} handover={handover} onDone={() => setView('mine')} onCancel={() => setView('browse')} />
      )}

      {view === 'mine' && <MyListings clubSlug={clubSlug} onList={() => setView('new')} onOpen={setMineItem} onSee={seeItem} initialChatId={initialChatId} />}

      {view === 'browse' && (
        <>
          <div className="kit-filters">
            <select className="input" aria-label="What" value={type} onChange={e => setType(e.target.value)}>
              <option value="all">Everything</option>
              {ITEM_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <select className="input" aria-label="Size" value={size} onChange={e => setSize(e.target.value)}>
              <option value="all">Any size</option>
              {(data?.sizes || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {isLoading && <div className="page-loading"><div className="spinner" /></div>}
          {!isLoading && listings.length === 0 && (
            <div className="empty-state">
              {type !== 'all' || size !== 'all'
                ? 'Nothing matches those filters right now.'
                : 'Nothing listed yet — be the first to pass something on.'}
            </div>
          )}

          <div className="kit-grid">
            {listings.map(l => (
              <button type="button" key={l.id} className={`kit-card card ${l.status === 'claimed' ? 'is-claimed' : ''}`} onClick={() => setOpenId(l.id)}>
                <KitPhoto url={l.photo_url} alt={itemLabel(l)} />
                {l.status === 'claimed' && <span className="kit-claimed-tag">{l.claimed_by_me ? 'Yours' : 'Claimed'}</span>}
                {l.unread_messages > 0 && <span className="kit-card-unread"><MessageCircle size={12} /> <UnreadBadge n={l.unread_messages} /></span>}
                {l.title ? (
                  <div className="kit-card-info">
                    <strong className="kit-item-name">{l.title}</strong>
                    <span className="kit-card-cond">{itemSubline(l)}</span>
                  </div>
                ) : (
                  <div className="kit-card-info">
                    <strong>{TYPE_LABEL[l.item_type]}</strong>
                    <span>{l.size}</span>
                    <span className="kit-card-cond">{CONDITION_LABEL[l.condition]}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
          {data?.truncated && <p className="kit-hint">Showing the newest {data.cap} items — use the filters to narrow it down.</p>}
        </>
      )}

      {open && (
        <ItemDetail clubSlug={clubSlug} item={open} viewer={viewer} handover={handover}
          onClose={() => setOpenId(null)} onChanged={refresh} onBlocked={onBlocked} />
      )}
      {!open && mineItem && (
        <ItemDetail clubSlug={clubSlug} item={mineItem} viewer={viewer} handover={handover}
          onClose={() => setMineItem(null)} onChanged={refresh} onBlocked={onBlocked} />
      )}
    </div>
  );
}
